/**
 * 1 对 1 语音：WebRTC P2P，信令复用现有的 WebSocket 中继（架构 §5.2）。
 *
 * 为什么不需要 SFU：只有两个人，P2P 打洞成功后延迟最低、服务器零流量。
 * 打洞失败时由 STUN 无解、必须靠 TURN——这里先用 Cloudflare + Google 的公共 STUN，
 * TURN 留到真正上线再加（`iceServers` 里加一组 turn 即可）。
 */

export type VoiceRole = 'offerer' | 'answerer';

export type VoiceState = 'idle' | 'requesting-mic' | 'connecting' | 'live' | 'failed';

interface SignalMessage {
  t: string;
  sdp?: RTCSessionDescriptionInit;
  cand?: RTCIceCandidateInit;
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.cloudflare.com:3478' },
  { urls: 'stun:stun.l.google.com:19302' },
];

/** 400 Hz 正弦波当麦克风：验证信令/连接/轨道，不依赖真实硬件 */
async function makeSyntheticMic(): Promise<MediaStream> {
  const ctx = new AudioContext();
  // 不要 await resume()：没有用户手势时它永远不 resolve，会把整条流程卡死
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  const osc = ctx.createOscillator();
  osc.frequency.value = 400;
  const gain = ctx.createGain();
  gain.gain.value = 0.02;
  const dest = ctx.createMediaStreamDestination();
  osc.connect(gain).connect(dest);
  osc.start();
  return dest.stream;
}

export class VoiceChat {
  /** 对端的音频元素（自动播放；被浏览器拦住时会在下一次点击时重试） */
  readonly audio: HTMLAudioElement;

  private pc: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private levelData = new Uint8Array(512);

  private pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteSet = false;
  private mutedFlag = false;
  private closed = false;

  state: VoiceState = 'idle';
  onState: ((state: VoiceState, detail?: string) => void) | null = null;

  constructor(
    private readonly send: (msg: unknown) => void,
    private readonly role: VoiceRole,
    /** 测试用：不走真实麦克风，而是一个振荡器（用来验证信令/连接，不依赖硬件） */
    private readonly syntheticMic = false,
  ) {
    this.audio = document.createElement('audio');
    this.audio.autoplay = true;
    this.audio.setAttribute('playsinline', '');
    this.audio.style.display = 'none';
    document.body.appendChild(this.audio);
  }

  get muted(): boolean {
    return this.mutedFlag;
  }

  private setState(state: VoiceState, detail?: string): void {
    this.state = state;
    this.onState?.(state, detail);
  }

  /** 申请麦克风 + 建连接。失败不抛：语音是可降级功能。 */
  async start(): Promise<void> {
    if (this.pc) return;
    this.setState('requesting-mic');

    try {
      // 回声消除必须开：VR 头显外放很容易啸叫
      this.localStream = this.syntheticMic
        ? await makeSyntheticMic()
        : await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
    } catch (err) {
      this.setState('failed', err instanceof Error ? err.name : 'mic-unavailable');
      return;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.pc = pc;
    this.setState('connecting');

    for (const track of this.localStream.getAudioTracks()) {
      pc.addTrack(track, this.localStream);
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) this.send({ t: 'rtc-ice', cand: e.candidate.toJSON() });
    };

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      if (stream) {
        this.audio.srcObject = stream;
        void this.audio.play().catch(() => {
          /* 自动播放被拦：等下一次用户点击 */
        });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') this.setState('live');
      else if (pc.connectionState === 'failed') this.setState('failed', 'ice-failed');
      else if (pc.connectionState === 'disconnected') this.setState('connecting', 'disconnected');
    };

    this.setupAnalyser(this.localStream);

    if (this.role === 'offerer') await this.makeOffer();
  }

  /** 本端麦克风的音量（0..1），用来驱动口型 */
  get level(): number {
    if (!this.analyser || this.mutedFlag) return 0;
    this.analyser.getByteTimeDomainData(this.levelData);
    let sum = 0;
    for (let i = 0; i < this.levelData.length; i++) {
      const v = (this.levelData[i]! - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.levelData.length);
    // 经验映射：说话 ≈ 0.05–0.2 RMS，放大到 0..1
    return Math.min(1, rms * 6);
  }

  private setupAnalyser(stream: MediaStream): void {
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      this.audioCtx = ctx;
      this.analyser = analyser;
      this.levelData = new Uint8Array(analyser.fftSize);
    } catch {
      /* 拿不到 AnalyserNode 就只是没有口型，不影响通话 */
    }
  }

  private async makeOffer(): Promise<void> {
    if (!this.pc) return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.send({ t: 'rtc-offer', sdp: offer });
  }

  async handleSignal(msg: SignalMessage): Promise<void> {
    const pc = this.pc;
    if (!pc) return;

    try {
      if (msg.t === 'rtc-offer' && msg.sdp && this.role === 'answerer') {
        await pc.setRemoteDescription(msg.sdp);
        this.remoteSet = true;
        await this.flushCandidates();
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.send({ t: 'rtc-answer', sdp: answer });
      } else if (msg.t === 'rtc-answer' && msg.sdp && this.role === 'offerer') {
        await pc.setRemoteDescription(msg.sdp);
        this.remoteSet = true;
        await this.flushCandidates();
      } else if (msg.t === 'rtc-ice' && msg.cand) {
        if (this.remoteSet) await pc.addIceCandidate(msg.cand);
        else this.pendingCandidates.push(msg.cand);
      }
    } catch (err) {
      this.setState('failed', err instanceof Error ? err.message : 'signal-error');
    }
  }

  private async flushCandidates(): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    const list = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const cand of list) {
      try {
        await pc.addIceCandidate(cand);
      } catch {
        /* 过期的 candidate 直接丢 */
      }
    }
  }

  setMuted(muted: boolean): void {
    this.mutedFlag = muted;
    if (this.localStream) {
      for (const track of this.localStream.getAudioTracks()) track.enabled = !muted;
    }
  }

  /** 需要用户手势后再尝试播放（绕过自动播放限制） */
  resumeAudio(): void {
    if (this.audio.srcObject) void this.audio.play().catch(() => {});
    if (this.audioCtx?.state === 'suspended') void this.audioCtx.resume();
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.pc?.close();
    this.audio.srcObject = null;
    this.audio.remove();
    void this.audioCtx?.close();
    this.setState('idle');
  }
}
